import { DateTime, IANAZone } from "luxon";

type CalendarJob = { date?: string; startDate?: string; endDate?: string };

const resolveZone = (timeZone?: string): string =>
  timeZone && IANAZone.isValidZone(timeZone)
    ? timeZone
    : Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

const civilDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

export const getCompanyToday = (timeZone?: string, now = new Date()): Date => {
  const today = DateTime.fromJSDate(now).setZone(resolveZone(timeZone));
  return new Date(today.year, today.month - 1, today.day);
};

export const getCompanyDateRange = (rangeStart: Date, rangeEnd: Date, timeZone?: string) => {
  const zone = resolveZone(timeZone);
  const start = DateTime.fromISO(civilDay(rangeStart), { zone }).startOf("day");
  const end = DateTime.fromISO(civilDay(rangeEnd), { zone }).plus({ days: 1 }).startOf("day");
  return { start: start.toUTC().toISO()!, end: end.toUTC().minus({ milliseconds: 1 }).toISO()! };
};

export const getCompanyMonthRange = (date: Date, timeZone?: string) => {
  const zone = resolveZone(timeZone);
  const start = DateTime.fromObject({ year: date.getFullYear(), month: date.getMonth() + 1, day: 1 }, { zone });
  const end = start.plus({ months: 1 });
  return { start: start.toUTC().toISO()!, end: end.toUTC().minus({ milliseconds: 1 }).toISO()! };
};

export const jobOverlapsRange = (job: CalendarJob, rangeStart: Date, rangeEnd: Date, timeZone?: string): boolean => {
  const start = new Date(job.startDate || job.date || "");
  const end = new Date(job.endDate || job.startDate || job.date || "");
  if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime())) return false;

  // The end instant is exclusive: a visit ending at midnight does not occupy
  // the following day.
  const endExclusive = Math.max(end.getTime(), start.getTime() + 1);
  const companyRange = getCompanyDateRange(rangeStart, rangeEnd, timeZone);
  return start.getTime() <= new Date(companyRange.end).getTime() &&
    endExclusive > new Date(companyRange.start).getTime();
};

export const jobOverlapsDay = (job: CalendarJob, day: Date, timeZone?: string): boolean =>
  jobOverlapsRange(job, day, day, timeZone);
